import {useTranslation} from 'react-i18next';
import {Navigate, useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import cn from 'clsx';
import {FeedbackPayload} from '@guestapp/sdk';
import {PATHS} from 'Routes';
import {QUERY_CACHE_KEYS} from 'utils/constants';
import {toastResponseError} from 'utils/common';
import {useReservation} from 'context/reservation';
import {useMutateCheckoutFeedback} from 'pages/Checkout/hooks/useMutateCheckoutFeedback';
import {usePath} from 'hooks/common';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {SimplePage} from 'components/SimplePage';
import RateForm, {type FormTypes as RateFormTypes} from 'components/RateForm';
import styles from './styles.module.css';

function RateYourStayView() {
  const {checkoutFlowStatus} = useCheckoutSettings();
  const navigate = useNavigate();
  const {reservationId} = useReservation();
  const {getFullPath} = usePath();
  const queryClient = useQueryClient();
  const {t} = useTranslation('check-out');
  const {createCheckoutFeedbackMutation, status} = useMutateCheckoutFeedback({
    onSuccess: (data: FeedbackPayload) => {
      queryClient.invalidateQueries([QUERY_CACHE_KEYS.reservation]);

      navigate(getFullPath(PATHS.checkout.thanks), {state: {rating: data.rating}});
    },
    onError: toastResponseError,
  });
  const onSubmit = (data: RateFormTypes) => {
    createCheckoutFeedbackMutation({
      reservation: reservationId ?? '',
      rating: data.rating,
      comment: data.comment ?? '',
    });
  };

  if (checkoutFlowStatus === 'FEEDBACK_SUBMITTED') {
    return <Navigate to={getFullPath(PATHS.checkout.main)} />;
  }

  return (
    <SimplePage
      title={t('rate_your_stay.title')}
      description={t('rate_your_stay.description')}
      className={cn('rate-your-stay-view-page', {
        'opacity-50 transition-all duration-500 ease-in-out pointer-events-none':
          status === 'loading',
        '': status === 'success' || status === 'idle',
      })}
    >
      <div className="flex items-center justify-center w-full rate-your-stay-view">
        <RateForm
          onSubmit={onSubmit}
          className={cn(
            styles.RateYourStayView__rateForm,
            'rate-your-stay-view__rate-form',
          )}
          rules={{
            rating: {required: true},
          }}
        />
      </div>
    </SimplePage>
  );
}

export default RateYourStayView;
